// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";

// XNG-Changes: add fields for goBackLink
const enum fields {
    overrideTitle = "overrideTitle",
    showGoBack = "showGoBack",
    goBackTitle = "goBackTitle",
    goBackLink = "goBackLink",
    variant = "variant",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.overrideTitle]: string;
        [fields.showGoBack]: boolean;
        [fields.goBackTitle]: string;
        [fields.goBackLink]: LinkFieldValue;
        [fields.variant]: keyof ThemeTypography;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PageTitleStyles {
    wrapper?: InjectableCss;
    title?: InjectableCss;
    goBackLink?: LinkPresentationProps;
}

export const pageTitleStyles: PageTitleStyles = {};

const PageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields }: Props) => {
    const { overrideTitle, showGoBack, goBackTitle, goBackLink, variant } = fields;
    const linkUrl = goBackLink ? (typeof goBackLink === "string" ? goBackLink : useGetLink(goBackLink).url) : "";
    const history = useHistory();

    const goBack = (event: React.MouseEvent<Element, MouseEvent>) => {
        event.preventDefault();
        if (linkUrl) {
            history.push(linkUrl);
            return;
        }
        if (typeof window === "undefined") {
            return;
        }
        window.history.back();
    };
    if (showGoBack) {
        return (
            <StyledWrapper {...pageTitleStyles.wrapper}>
                <Typography {...pageTitleStyles.title} variant={variant} as="h1" data-test-selector="PageTitle">
                    {overrideTitle || pageTitle}
                </Typography>
                <Link onClick={goBack} {...pageTitleStyles.goBackLink} aria-label={`${goBackTitle}`}>
                    {goBackTitle}
                </Link>
            </StyledWrapper>
        );
    }

    // XNG-Change: Show it as h1 with variant h1
    return (
        <Typography variant={variant} as="h1" data-test-selector="PageTitle">
            {overrideTitle || pageTitle}
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        // XNG-Change
        fieldDefinitions: [
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.showGoBack,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.goBackTitle,
                editorTemplate: "TextField",
                defaultValue: "Go Back",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.showGoBack,
            },
            {
                name: fields.goBackLink,
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.showGoBack,
            },
            {
                name: fields.variant,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "H1",
                        value: "h1",
                    },
                    {
                        displayName: "H2",
                        value: "h2",
                    },
                    {
                        displayName: "H3",
                        value: "h3",
                    },
                ],
                isRequired: true,
                defaultValue: "h1",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
